import gestionApiService from '@/core/services/gestionApiService.ts'
import { validateSchemaAsync } from '@/core/utils/safeSchemaValidations.ts'
import {
  ArticleDetailDTO,
  ArticleDTO,
  ChangeArticleDto,
  DeliveryArticleList,
  DeliveryArticleListSchema,
  EditArticleDto,
  EnrolledHoursResponse,
  enrolledHoursResponseSchema,
  LiberateArticleDto,
  MosyleDeviceLookupResponse,
  mosyleDeviceLookupResponseSchema,
  MotorArticleDTO,
  OtherArticleDTO
} from '@/modules/sales/clients/types/Article.ts'
import {
  ArticleListResponse,
  articleListResponseSchema,
  articleResponseSchema,
  AssignedArticleAppleListResponse,
  assignedArticleAppleResponseSchema,
  AssignedArticleListResponse,
  AssignedArticleMotorListResponse,
  assignedArticleMotorResponseSchema,
  assignedArticleResponseSchema,
  ClientListResponse,
  clientListResponseSchema
} from '@/modules/sales/clients/services/apiValidation/apiValidationSchemas.ts'
import {
  CreditAppleResponse,
  CreditAppleResponseSchema,
  CreditDetailsResponse,
  CreditDetailsResponseSchema,
  ModelDeviceResponse,
  ModelDeviceResponseSchema,
  NuovoDeviceResponse,
  NuovoDeviceResponseSchema
} from '@/modules/sales/clients/types/ChangeCredit.ts'
import {
  quotesResponse,
  quotesResponseSchema
} from '@/modules/sales/clients/types/Quote.ts'
import {
  SupplierResponseSchema,
  supplierResponseSchema
} from '@/modules/sales/clients/services/apiValidation/supplierApiValidation.ts'
import {
  EqualityDeviceInfoResponse,
  equalityDeviceInfoResponseSchema
} from '@/modules/sales/clients/services/apiValidation/equalityDeviceInfoApiValidation.ts'
import {
  QRImageDataApiValidationResponse,
  qrImageDataApiValidationResponseSchema
} from '@/modules/sales/clients/services/apiValidation/qrImageDataApiValidation.ts'
import {
  requireOTPApiValidationResponseSchema,
  RequireOTPResponse
} from '@/modules/sales/clients/services/apiValidation/requireOTPApiValidation.ts'
import {
  CreditUnpayResponse,
  creditUnpayResponseSchema
} from '@/modules/sales/clients/services/apiValidation/creditUnpayApiValidation.ts'
import {
  MundoDeviceResponse,
  MundoDeviceResponseSchema
} from '@/modules/sales/clients/services/apiValidation/mundoDeviceApiValidation.ts'
import {
  MotorLetterResponse,
  motorLetterResponseSchema
} from '@/modules/sales/clients/services/apiValidation/motorLetterApiValidation.ts'
import {
  AssignDeviceToClientResponse,
  assignDeviceToClientResponseSchema
} from '@/modules/sales/clients/services/apiValidation/assignDeviceToClientApiValidation.ts'
import {
  OriginCertificateResponse,
  originCertificateResponseSchema
} from '@/modules/sales/clients/services/apiValidation/originCertificateApiValidation.ts'
import { getBase64 } from '@/core/utils/files.ts'
import {
  CreditPreassignedCreatedResponse,
  creditPreassignedCreatedResponseSchema,
  CreditPreassignedDetailResponse,
  creditPreassignedDetailResponseSchema
} from '@/modules/sales/clients/services/apiValidation/creditPreassignedApiValidation.ts'
import { CreditPreassignedRequestDTO } from '@/modules/sales/clients/types/CreditPreassigned.ts'
import {
  RefinancePayload,
  RefinanceQuotesResponse,
  refinanceQuotesResponseSchema
} from '@/modules/sales/clients/types/Refinance.ts'

const listArticles = (filters: string) =>
  validateSchemaAsync(
    articleListResponseSchema,
    gestionApiService.httpGET<ArticleListResponse>(
      `credits${filters ? filters : ''}`
    )
  )

const getArticle = (id: string) =>
  validateSchemaAsync(
    articleResponseSchema,
    gestionApiService.httpGET<ArticleDetailDTO>(`credits/${id}/details`)
  )

const releaseArticle = (id: string | number) =>
  gestionApiService.httpPOST(`credits/release/${id}`, {})

const deleteArticle = (id: string | number) =>
  gestionApiService.httpDELETE(`credits/${id}`)

const getClientsThatHaveArticles = (filters: string) =>
  validateSchemaAsync(
    clientListResponseSchema,
    gestionApiService.httpGET<ClientListResponse>(
      `credits${filters ? filters : ''}`
    )
  )

const getArticleByRegisterNumber = (
  registerNumber: string,
  clientId: string,
  params?: string
) =>
  validateSchemaAsync(
    assignedArticleResponseSchema,
    gestionApiService.httpGET<AssignedArticleListResponse>(
      `/credits/by-register-number/${registerNumber}/${clientId}${params ? params : ''}`
    )
  )

const getAutomotiveByBodySerial = (
  engineSerial: string,
  clientId: string,
  params?: string
) =>
  validateSchemaAsync(
    assignedArticleMotorResponseSchema,
    gestionApiService.httpGET<AssignedArticleMotorListResponse>(
      `/credits/by-body-serial/automotive-product/${engineSerial}/${clientId}${params ? params : ''}`
    )
  )

const getArticleAppleByRegisterNumber = (
  registerNumber: string,
  clientId: string,
  isEnrolledMoreThan30Days?: boolean
) =>
  validateSchemaAsync(
    assignedArticleAppleResponseSchema,
    gestionApiService.httpPOST<AssignedArticleAppleListResponse>(
      '/credits/by-register-number/apple-product',
      {
        registerNumber,
        clientId,
        ...(isEnrolledMoreThan30Days == null
          ? {}
          : { isEnrolledMoreThan30Days })
      }
    )
  )

const assignArticle = <
  T extends ArticleDTO | OtherArticleDTO | MotorArticleDTO
>(
  article: T,
  otp?: string
) =>
  validateSchemaAsync(
    assignDeviceToClientResponseSchema,
    gestionApiService.httpPOST<AssignDeviceToClientResponse>(
      'credits/assign-device-to-client',
      {
        ...article,
        code: otp,
        unpaidCredit: !!article.unpaidCredit
      }
    )
  )

const reassignArticle = (creditId: string | null, cedule: string) =>
  gestionApiService.httpPOST<void>(`/credits/reassign/${creditId}`, {
    documentNumber: cedule
  })

const changeRequestArticle = (payload: ChangeArticleDto) =>
  gestionApiService.httpPOST<void>(`/credit-change-requests`, {
    ...payload
  })

const changeArticle = (id: string, payload: ChangeArticleDto) =>
  gestionApiService.httpPOST<void>(`/credits/exchange/${id}`, {
    ...payload
  })

const createEditArticleRequest = (payload: EditArticleDto) =>
  gestionApiService.httpPOST<void>(`credit-editing-requests`, payload)

const createLiberateArticleRequest = (payload: LiberateArticleDto) =>
  gestionApiService.httpPOST('warranty-requests', payload)

const getArticleNuovoByRegisterNumber = (registerNumber: string) =>
  validateSchemaAsync(
    NuovoDeviceResponseSchema,
    gestionApiService.httpGET<NuovoDeviceResponse>(
      `devices/info/${registerNumber}`
    )
  )

const getArticleMundoByRegisterNumber = (
  registerNumber: string,
  clientId: string
) =>
  validateSchemaAsync(
    MundoDeviceResponseSchema,
    gestionApiService.httpGET<MundoDeviceResponse>(
      `mundo-grupo/devices/info/${registerNumber}?clientId=${clientId}`
    )
  )

const getArticleEqualityByImei = (imei: string, clientId: string) =>
  validateSchemaAsync(
    equalityDeviceInfoResponseSchema,
    gestionApiService.httpGET<EqualityDeviceInfoResponse>(
      `equality/devices/info/${imei}?clientId=${clientId}`
    )
  )

const getArticleEqualityDetailsByImei = (imei: string) =>
  validateSchemaAsync(
    equalityDeviceInfoResponseSchema,
    gestionApiService.httpGET<EqualityDeviceInfoResponse>(
      `equality/devices/details/${imei}`
    )
  )

const getAppleByRegisterNumber = (registerNumber: string) =>
  validateSchemaAsync(
    CreditAppleResponseSchema,
    gestionApiService.httpGET<CreditAppleResponse>(
      `apple-products/${registerNumber}/info`
    )
  )

const getBrandModelByNuovo = (modelNuovo: string) =>
  validateSchemaAsync(
    ModelDeviceResponseSchema,
    gestionApiService.httpGET<ModelDeviceResponse>(`models/nuovo/${modelNuovo}`)
  )

const getCreditCuotes = (creditId: string) =>
  validateSchemaAsync(
    quotesResponseSchema,
    gestionApiService.httpGET<quotesResponse>(`credits/${creditId}/cuotes`)
  )

const getCreditDetails = (creditId: string) =>
  validateSchemaAsync(
    CreditDetailsResponseSchema,
    gestionApiService.httpGET<CreditDetailsResponse>(
      `credits/${creditId}/details`
    )
  )

const deleteArticleRequest = (id: string, reason: string) => {
  const body = {
    creditId: id,
    deletionRequest: reason
  }
  return gestionApiService.httpPOST('credit-deletion-requests', body)
}

const deleteUnpayArticle = (id: string) =>
  gestionApiService.httpDELETE(`credit_unpay/${id}`)

const sendMotorCirculationByEmail = (creditId: string) => {
  return gestionApiService.httpGET(
    `credits/send-vehicle-registration/${creditId}`
  )
}

const downloadMotorLetter = (creditId: string) =>
  validateSchemaAsync(
    motorLetterResponseSchema,
    gestionApiService.httpGET<MotorLetterResponse>(
      `credits/${creditId}/moto-sale-contract`
    )
  )

const downloadUnpayMotorLetter = (creditUnpayId: string) =>
  validateSchemaAsync(
    motorLetterResponseSchema,
    gestionApiService.httpGET<MotorLetterResponse>(
      `credit_unpay/${creditUnpayId}/moto-sale-contract`
    )
  )

const getBuyRequest = (creditId: string | null | undefined, filters: string) =>
  validateSchemaAsync(
    DeliveryArticleListSchema,
    gestionApiService.httpGET<DeliveryArticleList>(
      `buy-requests/by-client/${creditId}${filters ? filters : ''}`
    )
  )

const getEnrolledHours = (registerNumber: string) =>
  validateSchemaAsync(
    enrolledHoursResponseSchema,
    gestionApiService.httpGET<EnrolledHoursResponse>(
      `credits/enrolled-hours/${registerNumber}`
    )
  )

const lookupMosyleDevice = (serialNumber: string) =>
  validateSchemaAsync(
    mosyleDeviceLookupResponseSchema,
    gestionApiService.httpPOST<MosyleDeviceLookupResponse>(
      'mosyle/devices/lookup',
      { serialNumber }
    )
  )

const completeReinstall = (registerNumber: string) =>
  gestionApiService.httpGET(`warranty-requests/${registerNumber}/complete`)

const updateRegisterNumber = (
  modelId: string,
  registerNumber: string,
  newRegisterNumber: string
) =>
  gestionApiService.httpPOST('credits/replace', {
    modelId,
    registerNumberOld: registerNumber,
    registerNumberNew: newRegisterNumber
  })

const removeDeviceFromNuovo = (registerNumber: string) =>
  gestionApiService.httpGET(`warranty-requests/${registerNumber}/delete`, {})

const resetPassword = (creditId: string) =>
  gestionApiService.httpGET(`credits/reset-password/${creditId}`)

const unblockArticle = (creditId: string) =>
  gestionApiService.httpGET(`credits/${creditId}/temporary-unlock`)

const setResetPasswordConf = (amount: number, days: number) =>
  gestionApiService.httpPOST(`penalty_quote_reset`, {
    penaltyDelay: amount,
    days
  })

const isClientEnabledForAssignment = (clientId: string) =>
  gestionApiService.httpGET<{ recentlyCreated?: boolean }>(
    `clients/${clientId}/check-assignment-availability`
  )

const preloadArchivedArticleData = (creditId: string) =>
  validateSchemaAsync(
    creditUnpayResponseSchema,
    gestionApiService.httpGET<CreditUnpayResponse>(`credit_unpay/${creditId}`)
  )

const getSupplierByBrand = (articleBrandId: string, clientId?: string) =>
  validateSchemaAsync(
    supplierResponseSchema,
    gestionApiService.httpGET<SupplierResponseSchema>(
      `suppliers/by-brand/${articleBrandId}${clientId ? `?clientId=${clientId}` : ''}`
    )
  )

const sendCode = (
  email: string,
  brandName: string,
  modelName: string,
  quoteNumber: number,
  frequencyDayNumber: number
) => {
  return gestionApiService.httpPOST(`credits/send-code`, {
    email,
    brandName,
    modelName,
    installments: quoteNumber,
    frequency: frequencyDayNumber
  })
}

const getQRImageData = (registerNumber: string) =>
  validateSchemaAsync(
    qrImageDataApiValidationResponseSchema,
    gestionApiService.httpGET<QRImageDataApiValidationResponse>(
      `devices/enrollment_details/${registerNumber}`
    )
  )

const checkClientRequireOTP = (clientId: string) =>
  validateSchemaAsync(
    requireOTPApiValidationResponseSchema,
    gestionApiService.httpGET<RequireOTPResponse>(
      `clients/${clientId}/otp_verify`
    )
  )

const readOriginCertificate = async (file: File) => {
  const base64 = await getBase64(file, true)
  return validateSchemaAsync(
    originCertificateResponseSchema,
    gestionApiService.httpPOST<OriginCertificateResponse>(
      'ocr/origin-certificate',
      {
        base64
      }
    )
  )
}

const createCreditPreassigned = (payload: CreditPreassignedRequestDTO) =>
  validateSchemaAsync(
    creditPreassignedCreatedResponseSchema,
    gestionApiService.httpPOST<CreditPreassignedCreatedResponse>(
      'credit-preassigned',
      payload
    )
  )

const getCreditPreassignedQR = (id: number): Promise<Blob> =>
  gestionApiService.httpGET<Blob>(
    `credit-preassigned/${id}/qr`,
    { responseType: 'blob' },
    (response) => response.data
  )

const getCreditPreassigned = (id: number) =>
  validateSchemaAsync(
    creditPreassignedDetailResponseSchema,
    gestionApiService.httpGET<CreditPreassignedDetailResponse>(
      `credit-preassigned/${id}`
    )
  )

const getRefinanceQuotes = (creditId: string) =>
  validateSchemaAsync(
    refinanceQuotesResponseSchema,
    gestionApiService.httpGET<RefinanceQuotesResponse>(
      `refinancing/${creditId}/quotes`
    )
  )

const refinanceCredit = (creditId: string, payload: RefinancePayload) =>
  gestionApiService.httpPUT(`refinancing/${creditId}/quotes`, payload)

const articleService = {
  listArticles,
  getArticle,
  createEditArticleRequest,
  createLiberateArticleRequest,
  releaseArticle,
  deleteArticle,
  getClientsThatHaveArticles,
  getArticleByRegisterNumber,
  getAutomotiveByBodySerial,
  getArticleAppleByRegisterNumber,
  assignArticle,
  reassignArticle,
  getArticleNuovoByRegisterNumber,
  getArticleMundoByRegisterNumber,
  getArticleEqualityByImei,
  getArticleEqualityDetailsByImei,
  getBrandModelByNuovo,
  changeArticle,
  getCreditCuotes,
  getCreditDetails,
  deleteArticleRequest,
  deleteUnpayArticle,
  changeRequestArticle,
  getBuyRequest,
  getAppleByRegisterNumber,
  getEnrolledHours,
  lookupMosyleDevice,
  completeReinstall,
  updateRegisterNumber,
  removeDeviceFromNuovo,
  resetPassword,
  unblockArticle,
  setResetPasswordConf,
  isClientEnabledForAssignment,
  preloadArchivedArticleData,
  getSupplierByBrand,
  sendCode,
  getQRImageData,
  checkClientRequireOTP,
  sendMotorCirculationByEmail,
  downloadMotorLetter,
  downloadUnpayMotorLetter,
  readOriginCertificate,
  createCreditPreassigned,
  getCreditPreassignedQR,
  getCreditPreassigned,
  getRefinanceQuotes,
  refinanceCredit
}

export default articleService
